import { Injectable } from '@angular/core';
import { fetchAuthSession } from 'aws-amplify/auth';
import PptxGenJS from 'pptxgenjs';
import {
  GeneratedScenario,
  GenerateScenariosRequest,
  GenerateScenariosResponse,
  GenerateModuleSummariesRequest,
  GenerateModuleSummariesResponse,
  GeneratedModuleSummary,
  GenerateExerciseDocumentRequest,
  GenerateExerciseDocumentResponseMarkdown,
  RedTeamCritiqueRequest,
  RedTeamCritiqueResponse,
  ApplyCritiqueRequest,
  ApplyCritiqueResponse,
  PlanPptSlidesRequest,
  PlanPptSlidesResponse,
  PlannedPptSlide,
  GeneratePptContentRequest,
  GeneratePptContentResponse,
  GeneratePptSlidesRequest,
  GeneratedPptContentSlide,
  PptSlideContent,
  GenerateCrisisImagesRequest,
  GenerateCrisisImagesResponse,
  CrisisImageGenerationResult,
} from '../models/wizard.models';
import config from '../../../../config';

@Injectable({
  providedIn: 'root',
})
export class ScenarioService {
  private apiUrl = `https://gpzlv0dk21.execute-api.eu-west-1.amazonaws.com/dev`;
  private readonly completionEventTypes = [
    'complete',
    'result',
    'done',
    'final',
  ];

  /**
   * Genera escenarios llamando a la API real (endpoint de streaming).
   *
   * @param payload Datos del wizard para generar escenarios
   * @param onToken Callback opcional para recibir cada token del stream
   * @returns Promise con la respuesta de la API
   */
  async generateScenariosFromAPI(
    payload: GenerateScenariosRequest,
    onToken?: (content: string) => void,
  ): Promise<GeneratedScenario[]> {
    try {
      const { events, tokenText } = await this.streamRequest(
        'generate-scenarios',
        payload,
        onToken,
      );

      const scenarios = this.pickArrayFromEvents<GeneratedScenario>(events, [
        'scenarios',
      ]);
      if (scenarios) {
        return scenarios;
      }

      const parsed = this.parseTokenText<GenerateScenariosResponse>(tokenText);
      if (Array.isArray(parsed)) {
        return parsed;
      }
      return parsed?.scenarios ?? [];
    } catch (error) {
      console.error('Error generating scenarios from API:', error);
      throw error;
    }
  }

  /**
   * Realiza una petición a un endpoint de streaming (`text/event-stream`).
   *
   * Se usa la Fetch API en lugar de HttpClient porque HttpClient (XHR) no
   * consume streams: bufferiza toda la respuesta y, al ser tratada como
   * event-stream, el navegador dispara una reconexión automática de la MISMA
   * URL que no pasa por el TokenInterceptor, por lo que va sin Authorization y
   * devuelve 401. Leyendo el stream con fetch y adjuntando el token manualmente
   * evitamos ese request fantasma.
   *
   * @param path Segmento final del endpoint bajo `/api/ai/stream/`
   * @param payload Cuerpo JSON de la petición
   * @param onToken Callback opcional para recibir cada token del stream
   * @param compress Si es `true`, el body se comprime con gzip (bytes crudos) y
   *   se envía con `Content-Encoding: gzip`. API Gateway debe incluir
   *   `content-encoding` en `Access-Control-Allow-Headers` para que el preflight
   *   CORS no lo bloquee, y descomprime el body antes de pasarlo a la Lambda.
   * @returns Eventos JSON parseados y el texto agregado de los tokens
   */
  private async streamRequest(
    path: string,
    payload: unknown,
    onToken?: (content: string) => void,
    compress = false,
    onEvent?: (event: Record<string, unknown>) => void,
    failOnErrorEvent = false,
  ): Promise<{ events: Array<Record<string, unknown>>; tokenText: string }> {
    const session = await fetchAuthSession();
    const accessToken = session.tokens?.accessToken?.toString();

    const body: BodyInit = compress
      ? await this.compressBody(JSON.stringify(payload))
      : JSON.stringify(payload);

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...(compress ? { 'Content-Encoding': 'gzip' } : {}),
      ...(accessToken ? { Authorization: accessToken } : {}),
    };

    const response = await fetch(`${this.apiUrl}/api/ai/stream/${path}`, {
      method: 'POST',
      headers,
      body,
    });

    if (!response.ok) {
      throw new Error(`${path} failed with status ${response.status}`);
    }

    if (!response.body) {
      throw new Error(`${path} returned an empty stream`);
    }

    return this.consumeStream(response.body, onToken, onEvent, failOnErrorEvent);
  }

  /**
   * Lee el ReadableStream de la respuesta y devuelve los eventos JSON parseados
   * (uno por línea / evento SSE) junto con el texto agregado de los tokens.
   * Reenvía el contenido de cada token al callback opcional para mostrar
   * progreso en la UI.
   */
  private async consumeStream(
    body: ReadableStream<Uint8Array>,
    onToken?: (content: string) => void,
    onEvent?: (event: Record<string, unknown>) => void,
    failOnErrorEvent = false,
  ): Promise<{ events: Array<Record<string, unknown>>; tokenText: string }> {
    const reader = body.getReader();
    const decoder = new TextDecoder();
    const events: Array<Record<string, unknown>> = [];
    let tokenText = '';
    let buffer = '';

    const processLine = (rawLine: string): void => {
      const event = this.parseStreamLine(rawLine);
      if (!event) {
        return;
      }

      events.push(event);
      onEvent?.(event);

      if (failOnErrorEvent && this.matchesEventType(event, ['error'])) {
        throw new Error(
          this.extractErrorMessageFromEvent(event) ??
            'The stream returned an error event.',
        );
      }

      if (event['type'] === 'token' && typeof event['content'] === 'string') {
        tokenText += event['content'];
        onToken?.(event['content']);
      }
    };

    try {
      while (true) {
        const { done, value } = await reader.read();
        if (done) {
          break;
        }

        buffer += decoder.decode(value, { stream: true });

        let newlineIndex = buffer.indexOf('\n');
        while (newlineIndex !== -1) {
          processLine(buffer.slice(0, newlineIndex));
          buffer = buffer.slice(newlineIndex + 1);
          newlineIndex = buffer.indexOf('\n');
        }
      }

      // Procesa cualquier resto que haya quedado sin salto de línea final.
      processLine(buffer);
    } finally {
      reader.releaseLock();
    }

    return { events, tokenText };
  }

  /**
   * Comprime un string con gzip (Compression Streams API) y devuelve los bytes
   * crudos (ArrayBuffer), listos para enviarse con `Content-Encoding: gzip`.
   */
  private async compressBody(body: string): Promise<ArrayBuffer> {
    const stream = new CompressionStream('gzip');
    const writer = stream.writable.getWriter();
    writer.write(new TextEncoder().encode(body));
    writer.close();

    const chunks: Uint8Array[] = [];
    const reader = stream.readable.getReader();
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      chunks.push(value);
    }

    const totalLength = chunks.reduce((sum, chunk) => sum + chunk.length, 0);
    const compressed = new Uint8Array(totalLength);
    let offset = 0;
    for (const chunk of chunks) {
      compressed.set(chunk, offset);
      offset += chunk.length;
    }
    return compressed.buffer as ArrayBuffer;
  }

  private parseStreamLine(rawLine: string): Record<string, unknown> | null {
    // Soporta tanto NDJSON como SSE (`data: {...}`).
    const line = rawLine.replace(/^data:\s*/, '').trim();
    if (!line || line === '[DONE]') {
      return null;
    }

    try {
      return JSON.parse(line) as Record<string, unknown>;
    } catch {
      // Línea parcial o no-JSON: se ignora.
      return null;
    }
  }

  /**
   * Busca el primer valor de tipo array entre las claves indicadas, revisando
   * también un posible objeto anidado `data`.
   */
  private pickArrayFromEvents<T>(
    events: Array<Record<string, unknown>>,
    keys: string[],
  ): T[] | null {
    for (const event of events) {
      const data = event['data'] as Record<string, unknown> | undefined;
      for (const key of keys) {
        if (Array.isArray(event[key])) {
          return event[key] as T[];
        }
        if (data && Array.isArray(data[key])) {
          return data[key] as T[];
        }
      }
    }
    return null;
  }

  /**
   * Busca el primer valor no vacío entre las claves indicadas, revisando
   * también un posible objeto anidado `data`.
   */
  private pickValueFromEvents<T>(
    events: Array<Record<string, unknown>>,
    keys: string[],
  ): T | undefined {
    for (const event of events) {
      const data = event['data'] as Record<string, unknown> | undefined;
      for (const key of keys) {
        if (event[key] !== undefined && event[key] !== null) {
          return event[key] as T;
        }
        if (data && data[key] !== undefined && data[key] !== null) {
          return data[key] as T;
        }
      }
    }
    return undefined;
  }

  /**
   * Devuelve el último evento cuyo `type` coincida con alguno de los indicados.
   */
  private findEventByType(
    events: Array<Record<string, unknown>>,
    types: string[],
  ): Record<string, unknown> | undefined {
    for (let index = events.length - 1; index >= 0; index -= 1) {
      if (this.matchesEventType(events[index], types)) {
        return events[index];
      }
    }
    return undefined;
  }

  private matchesEventType(
    event: Record<string, unknown>,
    types: string[],
  ): boolean {
    const directType = event['type'];
    if (typeof directType === 'string' && types.includes(directType)) {
      return true;
    }

    const data = event['data'];
    if (data && typeof data === 'object' && !Array.isArray(data)) {
      const nestedType = (data as Record<string, unknown>)['type'];
      if (typeof nestedType === 'string' && types.includes(nestedType)) {
        return true;
      }
    }

    return false;
  }

  private extractErrorMessageFromEvent(
    event: Record<string, unknown>,
  ): string | null {
    const candidates: unknown[] = [event['message'], event['error'], event['content']];

    const data = event['data'];
    if (data && typeof data === 'object' && !Array.isArray(data)) {
      const nested = data as Record<string, unknown>;
      candidates.push(nested['message'], nested['error'], nested['content']);
    }

    for (const value of candidates) {
      if (typeof value === 'string' && value.trim()) {
        return value.trim();
      }
    }

    return null;
  }

  private parseTokenText<T>(tokenText: string): T | null {
    if (!tokenText.trim()) {
      return null;
    }

    try {
      return JSON.parse(tokenText) as T;
    } catch {
      console.warn('Could not parse aggregated stream tokens as JSON.');
      return null;
    }
  }

  /**
   * Genera resúmenes de módulos llamando a la API real (streaming).
   * @param payload Datos del wizard para generar módulos
   * @param onToken Callback opcional para recibir cada token del stream
   * @returns Promise con la respuesta de la API
   */
  async generateModuleSummariesFromAPI(
    payload: GenerateModuleSummariesRequest,
    onToken?: (content: string) => void,
  ): Promise<GeneratedModuleSummary[]> {
    try {
      const { events, tokenText } = await this.streamRequest(
        'generate-module-summaries',
        payload,
        onToken,
      );

      const modules = this.pickArrayFromEvents<GeneratedModuleSummary>(events, [
        'moduleReviews',
        'modules',
      ]);
      if (modules) {
        return modules;
      }

      const parsed =
        this.parseTokenText<GenerateModuleSummariesResponse>(tokenText);
      if (Array.isArray(parsed)) {
        return parsed;
      }
      return parsed?.moduleReviews ?? parsed?.modules ?? [];
    } catch (error) {
      console.error('Error generating module summaries from API:', error);
      throw error;
    }
  }

  /**
   * Genera el documento de ejercicio completo llamando a la API real (streaming).
   * @param payload Datos completos del wizard para generar el documento
   * @param onToken Callback opcional para recibir cada token del stream
   * @returns Promise con la respuesta de la API
   */
  async generateExerciseDocumentFromAPI(
    payload: GenerateExerciseDocumentRequest,
    onToken?: (content: string) => void,
  ): Promise<GenerateExerciseDocumentResponseMarkdown> {
    try {
      const { events, tokenText } = await this.streamRequest(
        'generate-exercise-document',
        payload,
        onToken,
      );

      const document = this.pickValueFromEvents<string>(events, ['document']);
      if (typeof document === 'string' && document) {
        return {
          type: 'complete',
          markdown: this.stripMarkdownCodeFence(document),
        };
      }

      const markdown = this.pickValueFromEvents<string>(events, ['markdown']);
      if (typeof markdown === 'string' && markdown) {
        const completeEvent = this.findEventByType(events, [
          'complete',
          'result',
        ]) as Partial<GenerateExerciseDocumentResponseMarkdown> | undefined;
        return {
          type: completeEvent?.type ?? 'complete',
          moduleCount: completeEvent?.moduleCount,
          markdown: this.stripMarkdownCodeFence(markdown),
        };
      }

      // Si no llegó un objeto estructurado, el markdown es el texto de tokens.
      return {
        type: 'complete',
        markdown: this.stripMarkdownCodeFence(tokenText),
      };
    } catch (error) {
      console.error('Error generating exercise document from API:', error);
      throw error;
    }
  }

  private stripMarkdownCodeFence(markdown: string): string {
    const trimmed = markdown.trim();
    const fenceMatch = trimmed.match(/^```markdown\n([\s\S]*?)\n```$/i);

    if (fenceMatch?.[1]) {
      return fenceMatch[1].trim();
    }

    return trimmed;
  }

  /**
   * Solicita crítica del Red Team sobre el documento de ejercicio (streaming).
   * @param payload Datos del documento y contexto para la crítica
   * @param onToken Callback opcional para recibir cada token del stream
   * @returns Promise con la respuesta de la API
   */
  async requestRedTeamCritiqueFromAPI(
    payload: RedTeamCritiqueRequest,
    onToken?: (content: string) => void,
  ): Promise<RedTeamCritiqueResponse> {
    try {
      const { events, tokenText } = await this.streamRequest(
        'red-team-critique',
        payload,
        onToken,
        true, // gzip + Content-Encoding; API Gateway descomprime antes de la Lambda
      );

      const critique = this.pickValueFromEvents<string>(events, ['critique']);
      const recommendations = this.pickArrayFromEvents<string>(events, [
        'recommendations',
      ]);

      if (typeof critique === 'string' && critique) {
        return {
          critique,
          recommendations: recommendations ?? undefined,
          targetLevel: this.pickValueFromEvents<string>(events, ['targetLevel']),
        };
      }

      return {
        critique: tokenText,
        recommendations: recommendations ?? undefined,
      };
    } catch (error) {
      console.error('Error requesting red team critique from API:', error);
      throw error;
    }
  }

  /**
   * Aplica la crítica del Red Team al documento y lo refina (streaming).
   *
   * El body se comprime con gzip; ver `streamRequest` para los requisitos de
   * CORS/API Gateway.
   *
   * @param payload Documento original y crítica para aplicar mejoras
   * @param onToken Callback opcional para recibir cada token del stream
   * @returns Promise con la respuesta de la API
   */
  async applyCritiqueFromAPI(
    payload: ApplyCritiqueRequest,
    onToken?: (content: string) => void,
  ): Promise<ApplyCritiqueResponse> {
    try {
      const { events, tokenText } = await this.streamRequest(
        'apply-critique',
        payload,
        onToken,
        true, // gzip + Content-Encoding; API Gateway descomprime antes de la Lambda
      );

      const document = this.pickValueFromEvents<string>(events, ['document']);
      const improvements = this.pickArrayFromEvents<string>(events, [
        'improvements',
      ]);

      if (typeof document === 'string' && document) {
        return {
          document: this.stripMarkdownCodeFence(document),
          improvements: improvements ?? undefined,
        };
      }

      return {
        document: this.stripMarkdownCodeFence(tokenText),
        improvements: improvements ?? undefined,
      };
    } catch (error) {
      console.error('Error applying critique from API:', error);
      throw error;
    }
  }

  /**
   * Genera plan de diapositivas PowerPoint basado en el documento (streaming).
   * @param payload Documento y contexto para generar el plan de slides
   * @param onToken Callback opcional para recibir cada token del stream
   * @returns Promise con la respuesta de la API
   */
  async planPptSlidesFromAPI(
    payload: PlanPptSlidesRequest,
    onToken?: (content: string) => void,
  ): Promise<PlanPptSlidesResponse> {
    try {
      const { events, tokenText } = await this.streamRequest(
        'plan-ppt-slides',
        payload,
        onToken,
        true, // gzip + Content-Encoding; API Gateway descomprime antes de la Lambda
      );

      const slidePlan = this.pickValueFromEvents<PlannedPptSlide[] | string>(
        events,
        ['slidePlan'],
      );
      if (slidePlan !== undefined) {
        return { slidePlan };
      }

      const slides = this.pickArrayFromEvents<PlannedPptSlide>(events, [
        'slides',
      ]);
      if (slides) {
        return slides;
      }

      return this.parseTokenText<PlanPptSlidesResponse>(tokenText) ?? [];
    } catch (error) {
      console.error('Error planning PPT slides from API:', error);
      throw error;
    }
  }

  /**
   * Genera contenido de diapositivas basado en el plan (streaming).
   * @param payload Documento, plan y contexto para generar contenido de slides
   * @param onToken Callback opcional para recibir cada token del stream
   * @returns Promise con arreglo de slides enriquecidas
   */
  async generatePptContentFromAPI(
    payload: GeneratePptContentRequest,
    onToken?: (content: string) => void,
    onEvent?: (event: Record<string, unknown>) => void,
  ): Promise<GeneratePptContentResponse> {
    try {
      const { events, tokenText } = await this.streamRequest(
        'generate-ppt-content',
        payload,
        onToken,
        true, // gzip + Content-Encoding; API Gateway descomprime antes de la Lambda
        onEvent,
        true,
      );

      console.log('📥 Raw generate-ppt-content stream response:', {
        events,
        tokenText,
      });

      const hasCompletionEvent = Boolean(
        this.findEventByType(events, this.completionEventTypes),
      );
      if (!hasCompletionEvent) {
        throw new Error(
          'generate-ppt-content stream closed before a completion event was received.',
        );
      }

      const slides = this.pickArrayFromEvents<GeneratedPptContentSlide>(events, [
        'slides',
        'content',
        'populatedSlides',
      ]);
      if (slides) {
        return slides;
      }

      const parsed = this.parseTokenText<
        | GeneratePptContentResponse
        | {
            slides?: GeneratePptContentResponse;
            populatedSlides?: GeneratePptContentResponse;
          }
      >(tokenText);
      if (Array.isArray(parsed)) {
        return parsed;
      }
      return parsed?.slides ?? parsed?.populatedSlides ?? [];
    } catch (error) {
      console.error('Error generating PPT content from API:', error);
      throw error;
    }
  }

  /**
   * Renderiza y descarga archivo PowerPoint final (streaming).
   * @param payload Contenido de slides y contexto
   * @returns Promise con blob y nombre sugerido de archivo
   */
  async generatePptSlidesFromAPI(
    payload: GeneratePptSlidesRequest,
  ): Promise<{ blob: Blob; filename: string }> {
    const fallbackFilename = this.buildPptFilename(payload.context);

    // El backend ya no expone `generate-ppt-slides`; renderizamos localmente
    // usando el contenido previamente generado por `generate-ppt-content`.
    return this.renderPptWithPptxGenJs(payload.slides, fallbackFilename);
  }

  private async renderPptWithPptxGenJs(
    slides: GeneratedPptContentSlide[],
    filename: string,
  ): Promise<{ blob: Blob; filename: string }> {
    const presentation = new PptxGenJS();
    presentation.layout = 'LAYOUT_WIDE';
    presentation.author = 'Crisis Exercise Tool';
    presentation.subject = 'Crisis exercise presentation';
    presentation.title = 'Crisis exercise presentation';

    slides
      .sort((left, right) => left.slideNumber - right.slideNumber)
      .forEach((slideData) => {
        const slide = presentation.addSlide();
        const normalizedLayout = (slideData.layout || 'title_and_bullets')
          .toLowerCase()
          .trim();

        slide.addText(slideData.title || `Slide ${slideData.slideNumber}`, {
          x: 0.5,
          y: 0.35,
          w: 12.3,
          h: 0.6,
          fontSize: 24,
          bold: true,
          color: '1F2937',
          fontFace: 'Calibri',
        });

        const bodyLines = this.getBodyLinesFromContent(
          slideData.content,
          normalizedLayout,
        );

        slide.addText(bodyLines.join('\n'), {
          x: 0.8,
          y: 1.2,
          w: 11.5,
          h: 5.4,
          fontSize: 16,
          color: '111827',
          valign: 'top',
          breakLine: true,
          fontFace: 'Calibri',
        });

        if (slideData.speakerNotes) {
          slide.addText(`Speaker Notes: ${slideData.speakerNotes}`, {
            x: 0.8,
            y: 6.8,
            w: 11.5,
            h: 0.45,
            fontSize: 10,
            color: '6B7280',
            italic: true,
            fontFace: 'Calibri',
          });
        }
      });

    if (slides.length === 0) {
      const slide = presentation.addSlide();
      slide.addText('No slide content was provided.', {
        x: 1.0,
        y: 3.0,
        w: 10.0,
        h: 0.8,
        fontSize: 22,
        bold: true,
        color: '374151',
        align: 'center',
      });
    }

    const blob = (await presentation.write({ outputType: 'blob' })) as Blob;
    return {
      blob,
      filename,
    };
  }

  private getBodyLinesFromContent(
    content: PptSlideContent,
    layout: string,
  ): string[] {
    const asRecord = (content ?? {}) as Record<string, unknown>;
    const bullets = this.readStringArray(asRecord['bullets']);
    const leftColumn = this.readStringArray(asRecord['leftColumn']);
    const rightColumn = this.readStringArray(asRecord['rightColumn']);
    const timelineItems = Array.isArray(asRecord['timelineItems'])
      ? (asRecord['timelineItems'] as Array<Record<string, unknown>>)
      : [];
    const headline =
      typeof asRecord['headline'] === 'string'
        ? asRecord['headline'].trim()
        : '';
    const moduleNumber =
      typeof asRecord['moduleNumber'] === 'string' ||
      typeof asRecord['moduleNumber'] === 'number'
        ? String(asRecord['moduleNumber'])
        : '';
    const timescale =
      typeof asRecord['timescale'] === 'string'
        ? asRecord['timescale'].trim()
        : '';
    const summary =
      typeof asRecord['summary'] === 'string' ? asRecord['summary'].trim() : '';

    if (layout === 'title' && headline) {
      return [headline];
    }

    if (layout === 'module_intro') {
      const lines: string[] = [];
      if (moduleNumber) {
        lines.push(`Module: ${moduleNumber}`);
      }
      if (timescale) {
        lines.push(`Timescale: ${timescale}`);
      }
      if (summary) {
        lines.push(summary);
      }
      if (lines.length > 0) {
        return lines;
      }
    }

    if (layout === 'timeline' && timelineItems.length > 0) {
      const lines = timelineItems
        .map((item) => {
          const time =
            typeof item['time'] === 'string'
              ? item['time'].trim()
              : 'Timeline item';
          const event =
            typeof item['event'] === 'string' ? item['event'].trim() : '';
          return event ? `- ${time}: ${event}` : `- ${time}`;
        })
        .filter((line) => line.trim().length > 0);

      if (lines.length > 0) {
        return lines;
      }
    }

    if (layout === 'two_column') {
      const lines: string[] = [];
      if (leftColumn.length > 0) {
        lines.push('Left Column');
        lines.push(...leftColumn.map((item) => `- ${item}`));
      }
      if (rightColumn.length > 0) {
        if (lines.length > 0) {
          lines.push('');
        }
        lines.push('Right Column');
        lines.push(...rightColumn.map((item) => `- ${item}`));
      }
      if (lines.length > 0) {
        return lines;
      }
    }

    if (bullets.length > 0) {
      return bullets.map((item) => `- ${item}`);
    }

    if (headline) {
      return [headline];
    }

    return this.flattenContentObject(asRecord);
  }

  private flattenContentObject(content: Record<string, unknown>): string[] {
    const entries = Object.entries(content)
      .map(([key, value]) => {
        if (value === null || value === undefined) {
          return '';
        }

        if (typeof value === 'string') {
          return `${key}: ${value}`;
        }

        if (typeof value === 'number' || typeof value === 'boolean') {
          return `${key}: ${String(value)}`;
        }

        if (Array.isArray(value)) {
          const serializedArray = value
            .map((item) => {
              if (typeof item === 'string') {
                return item;
              }

              try {
                return JSON.stringify(item);
              } catch {
                return String(item);
              }
            })
            .join(', ');
          return `${key}: ${serializedArray}`;
        }

        try {
          return `${key}: ${JSON.stringify(value)}`;
        } catch {
          return `${key}: ${String(value)}`;
        }
      })
      .filter((line) => line.trim().length > 0);

    return entries.length > 0 ? entries : ['No content provided.'];
  }

  private readStringArray(value: unknown): string[] {
    if (!Array.isArray(value)) {
      return [];
    }

    return value
      .map((item) => (typeof item === 'string' ? item.trim() : ''))
      .filter((item) => item.length > 0);
  }

  private buildPptFilename(
    context: GeneratePptSlidesRequest['context'],
  ): string {
    const scenarioTitle = context.selectedScenario?.title || 'crisis-exercise';
    const safeTitle = scenarioTitle
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, 50);

    return `${safeTitle || 'crisis-exercise'}-presentation.pptx`;
  }

  /**
   * Genera imágenes de crisis basadas en el escenario y módulos (streaming).
   *
   * El stream emite eventos `progress`, luego un evento `result` por cada imagen
   * (con `result.imageData` en base64) y finalmente un evento `complete`. El
   * array `results` del evento `complete` NO incluye `imageData`, por lo que las
   * imágenes se reconstruyen a partir de los eventos `result` individuales.
   *
   * @param payload Escenario y módulos para generar imágenes
   * @param onToken Callback opcional para recibir cada token del stream
   * @returns Promise con la respuesta de la API
   */
  async generateCrisisImagesFromAPI(
    payload: GenerateCrisisImagesRequest,
    onToken?: (content: string) => void,
  ): Promise<GenerateCrisisImagesResponse> {
    try {
      const { events } = await this.streamRequest(
        'generate-crisis-images',
        payload,
        onToken,
      );

      const results = events
        .filter((event) => event['type'] === 'result')
        .map((event) => event['result'])
        .filter(
          (result): result is CrisisImageGenerationResult =>
            result !== null && typeof result === 'object',
        );

      const finalEvent = this.findEventByType(events, [
        'complete',
        'partial',
        'error',
      ]) as Partial<GenerateCrisisImagesResponse> | undefined;

      // Si no llegaron eventos `result` individuales, usamos el array del
      // evento final como último recurso (sin imageData).
      const fallbackResults =
        results.length === 0
          ? (this.pickArrayFromEvents<CrisisImageGenerationResult>(events, [
              'results',
            ]) ?? [])
          : results;

      return {
        type: finalEvent?.type ?? (results.length > 0 ? 'complete' : 'error'),
        generationScope: finalEvent?.generationScope ?? payload.generationScope,
        results: fallbackResults,
      };
    } catch (error) {
      console.error('Error generating crisis images from API:', error);
      throw error;
    }
  }
}
